import type { UTCDate } from '@date-fns/utc';
import { isBefore } from 'date-fns';

import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import {
  PreferenceScope,
  UserPreferenceKey,
} from '../../../../shared/user-preferences/types';
import { PreferenceBrowserStorage } from '../../../../shared/user-preferences/utils/preference-session-storage';
import {
  type DateRangeFilter,
  type Filter,
  FilterId,
  type FilterSelectionsMap,
  type MultiFilter,
  type SingleValueFilter,
  type TreeFilter,
} from '../types/filters';
import { getRoleDefaultSelectionsForTaxonomy } from '../utils/getRoleDefaultSelectionsForTaxonomy';
import { getActiveSelections } from '../utils/primaryFilterUtils';

const normalizeRoleTaxonomySelection = (
  selection:
    | CustomRoleTaxonomySelection
    | { default: true }
    | 'adaptive'
    | undefined
): CustomRoleTaxonomySelection => {
  if (!selection || selection === 'adaptive') return { default: true };
  return selection as CustomRoleTaxonomySelection;
};

const areRoleTaxonomiesEquivalent = (
  a: CustomRoleTaxonomySelection,
  b: CustomRoleTaxonomySelection
): boolean => {
  if ('default' in a && a.default) {
    return 'default' in b && b.default === true;
  }
  if ('default' in b && b.default) return false;

  if ('taxonomyId' in a && typeof a.taxonomyId !== 'undefined') {
    return (
      'taxonomyId' in b &&
      typeof b.taxonomyId !== 'undefined' &&
      Number(b.taxonomyId) === Number(a.taxonomyId)
    );
  }

  if ('ricsK50Id' in a && a.ricsK50Id) {
    return 'ricsK50Id' in b && b.ricsK50Id === a.ricsK50Id;
  }

  if ('ricsK10Id' in a && a.ricsK10Id) {
    return 'ricsK10Id' in b && b.ricsK10Id === a.ricsK10Id;
  }

  return false;
};

export type FilterTransformer = (
  triggerFilter: Filter,
  currentDependentFilterState: Filter | undefined,
  fullFilterMap: FilterSelectionsMap
) => Partial<Filter> | null;

export interface FilterDependency {
  dependentFilterId: FilterId;
  transformer: FilterTransformer;
}

export const FILTER_DEPENDENCIES: Map<FilterId, FilterDependency[]> = new Map([
  [
    FilterId.SNAPSHOT_DATE,
    [
      {
        dependentFilterId: FilterId.DATE_RANGE,
        transformer: (triggerFilter, dependentFilter) => {
          const snapshotDate = (
            triggerFilter as SingleValueFilter<UTCDate | null>
          )?.value;
          const dateRangeFilter = dependentFilter as DateRangeFilter;

          if (snapshotDate === null) {
            return {
              ...dateRangeFilter,
              id: FilterId.DATE_RANGE,
              min: null,
              max: null,
            };
          }

          if (
            dateRangeFilter.max === null ||
            isBefore(snapshotDate, dateRangeFilter.max)
          ) {
            return {
              ...dateRangeFilter,
              id: FilterId.DATE_RANGE,
              min: snapshotDate,
              max: dateRangeFilter.max,
            };
          }

          return {
            ...dateRangeFilter,
            id: FilterId.DATE_RANGE,
            min: dateRangeFilter.min,
            max: null,
          };
        },
      },
    ],
  ],
  [
    FilterId.DATE_RANGE,
    [
      {
        dependentFilterId: FilterId.SNAPSHOT_DATE,
        transformer: (triggerFilter, dependentFilter) => {
          const dateRangeFilter = triggerFilter as DateRangeFilter;
          const snapshotDateFilter =
            dependentFilter as SingleValueFilter<UTCDate | null>;

          return {
            ...snapshotDateFilter,
            id: FilterId.SNAPSHOT_DATE,
            value: dateRangeFilter.min,
          };
        },
      },
    ],
  ],
  // TODO: Can we remove this dependency when we have role_taxonomy defined in one place?
  [
    FilterId.ROLE_TAXONOMY,
    [
      {
        dependentFilterId: FilterId.ROLE,
        transformer: (triggerFilter, currentDependentFilterState) => {
          const taxonomyFilter =
            triggerFilter as SingleValueFilter<CustomRoleTaxonomySelection>;
          const roleFilter = currentDependentFilterState as
            | TreeFilter
            | undefined;

          // When ROLE is primary (role page): apply taxonomy defaults
          if (roleFilter?.isPrimary) {
            const rolePrefs = PreferenceBrowserStorage.get(
              PreferenceScope.ROLE
            );
            const storedDefaults =
              rolePrefs[UserPreferenceKey.DefaultFilters] ?? [];
            const storedTaxonomyPref = normalizeRoleTaxonomySelection(
              rolePrefs[UserPreferenceKey.Taxonomies]?.role
            );
            const currentTaxonomy = normalizeRoleTaxonomySelection(
              taxonomyFilter.value
            );

            const shouldUseStoredDefaults =
              storedDefaults.length > 0 &&
              areRoleTaxonomiesEquivalent(storedTaxonomyPref, currentTaxonomy);

            const defaults = shouldUseStoredDefaults
              ? storedDefaults
              : getRoleDefaultSelectionsForTaxonomy(taxonomyFilter.value);

            // Preserve the active count (min 1) so a limit-1 page (role overview
            // / transitions) keeps one active role instead of activating every
            // default.
            const previousActiveCount = roleFilter?.primarySelections
              ? roleFilter.primarySelections.filter((s) => s.isActive).length
              : 1;

            const primarySelections = defaults.map((d, idx) => ({
              ...d,
              isActive: idx < Math.max(1, previousActiveCount),
            }));

            const update: Partial<TreeFilter> = {
              id: FilterId.ROLE,
              selections: getActiveSelections(primarySelections),
              isUserSubmitted: false,
              primarySelections,
            };

            return update;
          }

          // When ROLE is secondary (non-role pages): clear any existing role selections
          if (roleFilter && roleFilter.selections.length > 0) {
            return {
              id: FilterId.ROLE,
              selections: [],
              isUserSubmitted: false,
            };
          }

          return null;
        },
      },
      {
        dependentFilterId: FilterId.MULTI_ROLE,
        transformer: (_triggerFilter, currentDependentFilterState) => {
          const multiRoleFilter = currentDependentFilterState as
            | MultiFilter<TreeFilter>
            | undefined;

          if (
            multiRoleFilter?.instances &&
            multiRoleFilter.instances.length > 0
          ) {
            return {
              id: FilterId.MULTI_ROLE,
              instances: [],
              isUserSubmitted: false,
            };
          }

          return null;
        },
      },
    ],
  ],
  [
    FilterId.SKILL_TAXONOMY,
    [
      {
        dependentFilterId: FilterId.CUSTOM_SKILL,
        transformer: (_triggerFilter, currentDependentFilterState) => {
          // There is no skill-primary page; whenever taxonomy changes, clear selections
          if (
            currentDependentFilterState &&
            'selections' in currentDependentFilterState &&
            currentDependentFilterState.selections.length > 0
          ) {
            return {
              id: FilterId.CUSTOM_SKILL,
              selections: [],
              isUserSubmitted: false,
            };
          }
          return null;
        },
      },
      {
        dependentFilterId: FilterId.MULTI_CUSTOM_SKILL,
        transformer: (_triggerFilter, currentDependentFilterState) => {
          // Whenever taxonomy changes, clear all instances
          if (
            currentDependentFilterState &&
            'instances' in currentDependentFilterState &&
            currentDependentFilterState.instances.length > 0
          ) {
            return {
              id: FilterId.MULTI_CUSTOM_SKILL,
              instances: [],
              isUserSubmitted: false,
            };
          }

          return null;
        },
      },
    ],
  ],
]);
